import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';
import { blogService } from '../services/blogService';
import { BlogPost as BlogPostType } from '../types/blog';
import { COLORS } from '../constants/colors';
import { useTranslation } from 'react-i18next';

const PostContainer = styled.article`
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const BackLink = styled(Link)`
  color: ${COLORS.PRIMARY};
  text-decoration: none;
  display: inline-block;
  margin-bottom: 1rem;

  &:hover {
    text-decoration: underline;
  }
`;

const PostFeaturedImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 2rem;
`;

const PostTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
  color: #1a1a1a;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PostMeta = styled.div`
  display: flex;
  gap: 1rem;
  color: #666;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const PostCategoryContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const PostCategory = styled.span`
  display: inline-block;
  background-color: ${COLORS.BLOG_PRIMARY_BACKGROUND};
  color: ${COLORS.PRIMARY_DARK};
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.875rem;
`;

const PostContent = styled.div`
  font-size: 1.125rem;
  line-height: 1.8;
  color: #333;
  margin-bottom: 3rem;

  h2 {
    font-size: 1.875rem;
    font-weight: 600;
    margin-top: 2rem;
    margin-bottom: 1rem;
    color: #1a1a1a;
  }

  h3 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
    color: #1a1a1a;
  }

  p {
    margin-bottom: 1.25rem;
  }

  ul, ol {
    margin-bottom: 1.25rem;
    padding-left: 2rem;
  }

  li {
    margin-bottom: 0.5rem;
  }

  a {
    color: #2563eb;
    text-decoration: underline;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 1.5rem 0;
  }

  blockquote {
    border-left: 4px solid #dbeafe;
    padding-left: 1rem;
    margin: 1.5rem 0;
    font-style: italic;
    color: #555;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const PostCTA = styled.div`
  background-color: ${COLORS.BLOG_PRIMARY_BACKGROUND};
  border-radius: 8px;
  padding: 2rem;
  margin-top: 3rem;
`;

const PostCTATitle = styled.h3`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
  color: #1a1a1a;
`;

const PostCTADescription = styled.p`
  margin-bottom: 1.5rem;
  line-height: 1.6;
  color: #333;
`;

const PostCTAButton = styled(Link)`
  display: inline-block;
  background-color: ${COLORS.PRIMARY};
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${COLORS.PRIMARY_DARK};
  }
`;

const ErrorMessage = styled.p`
  color: #dc2626;
  text-align: center;
  padding: 2rem;
`;

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPostType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { lang } = useParams<{ lang?: string }>();
  const { t } = useTranslation();
  
  const language = lang || 'en';

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return;
      
      try {
        setLoading(true);
        const data = await blogService.getPostBySlug(slug, language);
        setPost(data);
      } catch (err) {
        setError('Failed to load blog post');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug, language]);

  // Update meta tags when post loads
  useEffect(() => {
    if (post) {
      document.title = `${post.title} | Storify Now`;
      
      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', post.metaDescription);

      // Update meta keywords
      if (post.metaKeywords) {
        let metaKeywords = document.querySelector('meta[name="keywords"]');
        if (!metaKeywords) {
          metaKeywords = document.createElement('meta');
          metaKeywords.setAttribute('name', 'keywords');
          document.head.appendChild(metaKeywords);
        }
        metaKeywords.setAttribute('content', post.metaKeywords.join(', '));
      }

      // Update Open Graph tags
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.setAttribute('content', post.title);

      let ogDescription = document.querySelector('meta[property="og:description"]');
      if (!ogDescription) {
        ogDescription = document.createElement('meta');
        ogDescription.setAttribute('property', 'og:description');
        document.head.appendChild(ogDescription);
      }
      ogDescription.setAttribute('content', post.metaDescription);

      if (post.featuredImage) {
        let ogImage = document.querySelector('meta[property="og:image"]');
        if (!ogImage) {
          ogImage = document.createElement('meta');
          ogImage.setAttribute('property', 'og:image');
          document.head.appendChild(ogImage);
        }
        ogImage.setAttribute('content', post.featuredImage);
      }
    }
  }, [post]);

  if (loading) {
    return (
      <PostContainer>
        <p>{t('blog.loadingPosts')}</p>
      </PostContainer>
    );
  }

  if (error || !post) {
    return (
      <PostContainer>
        <ErrorMessage>{error || 'Post not found'}</ErrorMessage>
        <BackLink to={language === 'ro' ? '/ro/blog' : '/blog'}>
          {t('blog.backToBlog')}
        </BackLink>
      </PostContainer>
    );
  }

  return (
    <PostContainer>
          <BackLink to={language === 'en' ? '/blog' : `/${language}/blog`}>
              ← {t('blog.backToBlog')}
          </BackLink>

      {post.featuredImage && (
        <PostFeaturedImage 
          src={post.featuredImage} 
          alt={post.title}
        />
      )}

      <PostTitle>{post.title}</PostTitle>

      <PostMeta>
        <span>
          {new Date(post.publishedDate).toLocaleDateString(
            language === 'ro' ? 'ro-RO' : 'en-US',
            { year: 'numeric', month: 'long', day: 'numeric' }
          )}
        </span>
        {post.author && <span>{t('blog.by')} {post.author}</span>}
        {post.readTime && <span>{post.readTime} {t('blog.minRead')}</span>}
      </PostMeta>

      {post.category && (
        <PostCategoryContainer>
          <PostCategory>{post.category}</PostCategory>
        </PostCategoryContainer>
      )}

      <PostContent dangerouslySetInnerHTML={{ __html: post.content }} />

      <PostCTA>
        <PostCTATitle>
          {t('blog.createStories')}
        </PostCTATitle>
        <PostCTADescription>
          {t('blog.tryStorify')}
        </PostCTADescription>
        <PostCTAButton to='/'>
          {t('blog.getStarted')}
        </PostCTAButton>
      </PostCTA>
    </PostContainer>
  );
};

export default BlogPost;